// Fase 15A — Attivazione account da invito (pubblica): l'invitato imposta la
// propria password (punto 11). Token monouso, scadenza, mai password via email.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Wordmark } from "@/components/landing/shared";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function AcceptInvite() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(formatApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Le password non coincidono");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await api.post(`/invitations/${token}/accept`, { password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6"
      data-testid="accept-invite-page">
      <Card className="w-full max-w-md border-slate-200 shadow-sm">
        <CardContent className="p-8">
          <div className="mb-6">
            <Wordmark />
          </div>

          {done ? (
            <div className="space-y-4" data-testid="invite-success">
              <h1 className="text-xl font-bold text-slate-900">Account attivato</h1>
              <p className="text-sm text-slate-600">
                Il tuo account è pronto. Accedi con la tua email e la password appena scelta.
              </p>
              <Link to="/login">
                <Button className="w-full bg-green-700 hover:bg-green-800"
                  data-testid="invite-goto-login">Vai al login</Button>
              </Link>
            </div>
          ) : error && !info ? (
            <div className="space-y-4" data-testid="invite-invalid">
              <h1 className="text-xl font-bold text-slate-900">Invito non valido</h1>
              <p className="text-sm text-red-700">{error}</p>
              <p className="text-xs text-slate-500">
                Chiedi all'amministratore di reinviarti l'invito: i link scadono dopo 7 giorni.</p>
            </div>
          ) : !info ? (
            <p className="text-sm text-slate-600" data-testid="invite-loading">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" aria-hidden="true" />
              Verifica invito…</p>
          ) : (
            <div className="space-y-4">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Attiva il tuo account</h1>
                <p className="text-sm text-slate-600 mt-1" data-testid="invite-summary">
                  {info.full_name} · {info.email} · ruolo {info.role}
                  {info.organization_name && ` · ${info.organization_name}`}
                  {info.company_name && ` · ${info.company_name}`}
                </p>
              </div>
              {error && (
                <div role="alert" data-testid="invite-error"
                  className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">
                  {error}</div>
              )}
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-password">Scegli la tua password</Label>
                  <Input id="invite-password" type="password" required minLength={8}
                    autoComplete="new-password" data-testid="invite-password-input"
                    value={password} onChange={(e) => setPassword(e.target.value)} />
                  <p className="text-[11px] text-slate-500">Minimo 8 caratteri.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-confirm">Conferma password</Label>
                  <Input id="invite-confirm" type="password" required minLength={8}
                    autoComplete="new-password" data-testid="invite-confirm-input"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>
                <Button type="submit" disabled={loading || password.length < 8}
                  className="w-full bg-green-700 hover:bg-green-800"
                  data-testid="invite-accept-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : "Attiva account"}
                </Button>
              </form>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
